import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import config from '../config';
import './Sidebar.css';

const PREFS_URL  = `${config.apiBase}/preferences.php`;
const UPLOAD_URL = `${config.apiBase}/upload_avatar.php`;
const AVATAR_BASE = config.apiBase.replace('/api', '');

const Sidebar = ({ user, setUser }) => {
  const [isCollapsed,  setIsCollapsed]  = useState(() => window.innerWidth <= 768);
  const [imgKey,       setImgKey]       = useState(Date.now());
  const [showSettings, setShowSettings] = useState(false);
  const [visibleApps,  setVisibleApps]  = useState(config.apps.map(a => a.key));
  const [alertCount, setAlertCount] = useState(0);

  const navigate = useNavigate();
  const accessibleApps = config.apps.filter(app => !app.adminOnly || String(user?.role).toLowerCase() === 'admin');

  const userLogin = user?.[config.loginField] || user?.username || user?.login || '';
  const userName  = user?.name || user?.nom || userLogin || 'Utilisateur';
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  const avatarUrl  = userLogin ? `${AVATAR_BASE}/assets/avatars/${userLogin}.jpg` : '';
  const fallbackUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=f06418&color=fff`;

  // Charger les préférences
  useEffect(() => {
    if (!userLogin) return;

    fetch(PREFS_URL, { headers:{ Authorization:`Bearer ${localStorage.getItem(config.tokenKey)||''}` } })
      .then(r => r.json())
      .then(data => {
        if (!data.success || !data.prefs) return;

        if (Array.isArray(data.prefs.visibleApps)) {
          const filtered = data.prefs.visibleApps.filter(k => config.apps.some(a => a.key === k));
          if (filtered.length) setVisibleApps(filtered);
          return;
        }

        // Fallback format objet { key: true/false }
        const apps = Object.keys(data.prefs)
          .filter(k => data.prefs[k] === true && config.apps.some(a => a.key === k));
        if (apps.length) setVisibleApps(apps);
      })
      .catch(err => console.error('Erreur prefs', err));
  }, [userLogin]);

  useEffect(() => {
    if (String(user?.role).toLowerCase() !== 'admin') return;
    let active = true;
    const update = async () => {
      try {
        const response = await fetch(`${config.apiBase}/alerts.php?summary=1`, { cache:'no-store', headers:{Authorization:`Bearer ${localStorage.getItem(config.tokenKey)||''}`} });
        const data = await response.json();
        if (response.status === 401 && active) {
          localStorage.removeItem(config.tokenKey);
          localStorage.removeItem(config.userKey);
          setUser(null);
          return;
        }
        if (active && response.ok) setAlertCount(Number(data.counts?.total||0));
      } catch {}
    };
    const fromPage = event => setAlertCount(Number(event.detail?.total||0));
    update();
    const interval = window.setInterval(update,30000);
    window.addEventListener('hrtp:alerts-updated',fromPage);
    return () => { active=false;window.clearInterval(interval);window.removeEventListener('hrtp:alerts-updated',fromPage); };
  },[user]);

  const savePreferences = async (newList) => {
    if (!userLogin) return;
    try {
      await fetch(PREFS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization:`Bearer ${localStorage.getItem(config.tokenKey)||''}` },
        body: JSON.stringify({ prefs: { visibleApps: newList } })
      });
    } catch (err) {
      console.error('Erreur save prefs', err);
    }
  };

  const toggleApp = (key) => {
    const next = visibleApps.includes(key)
      ? visibleApps.filter(k => k !== key)
      : [...visibleApps, key];
    setVisibleApps(next);
    savePreferences(next);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !userLogin) return;

    const formData = new FormData();
    formData.append('avatar', file);
    try {
      const res  = await fetch(UPLOAD_URL, { method: 'POST', headers:{Authorization:`Bearer ${localStorage.getItem(config.tokenKey)||''}`}, body: formData });
      const data = await res.json();
      if (data.success) {
        setImgKey(Date.now());
        alert('Photo mise à jour !');
      } else {
        alert(data.message || 'Impossible de mettre à jour la photo.');
      }
    } catch (err) {
      console.error('Erreur upload', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(config.tokenKey);
    localStorage.removeItem(config.userKey);
    setUser(null);
    navigate('/login');
  };

  return (
    <>
      <aside className={`stat-card sidebar-container ${isCollapsed ? 'collapsed' : ''}`}>

        <button
          className="toggle-btn"
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? 'Ouvrir le menu' : 'Fermer le menu'}
        >
          {isCollapsed ? '›' : '‹'}
        </button>

        <div className="sidebar-header">
          <div className="sidebar-brand">
            <img src="/hrtp-logo.png" alt="HRTP" />
            {!isCollapsed && <span>Portail métiers</span>}
          </div>

          {!isCollapsed && userLogin && (
            <div className="sidebar-user-card">
              <div className="profile-picture-container">
                <img
                  key={imgKey}
                  src={`${avatarUrl}?t=${imgKey}`}
                  alt={`Photo de ${userName}`}
                  className="profile-img"
                  onError={e => { e.target.onerror = null; e.target.src = fallbackUrl; }}
                />
                <label className="avatar-upload-btn" title="Changer la photo">
                  📷
                  <input type="file" accept="image/jpeg,.jpg,.jpeg" onChange={handleImageUpload} hidden />
                </label>
              </div>
              <div className="sidebar-user-copy">
                <small>Espace de travail</small>
                <strong>{userName}</strong>
                <span>{isAdmin ? 'Administrateur' : 'Utilisateur'}</span>
              </div>
              <button
                className="settings-icon-btn"
                onClick={() => setShowSettings(true)}
                title="Applications visibles"
                aria-label="Choisir les applications visibles"
              >
                ⚙
              </button>
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          {accessibleApps
            .filter(app => app.native || visibleApps.includes(app.key))
            .map(app => (
              <NavLink
                key={app.key}
                to={`/${app.key}`}
                className="btn nav-btn"
                title={app.label}
              >
                <span className="nav-icon">{app.icon}</span>
                {!isCollapsed && <span className="nav-label">{app.label}</span>}
                {app.key==='alertes'&&alertCount>0&&<b className="sidebar-alert-count">{alertCount>99?'99+':alertCount}</b>}
              </NavLink>
            ))}
        </nav>

        <button onClick={handleLogout} className="btn logout-btn">
          <span className="nav-icon">↪</span> {!isCollapsed && <span className="nav-label">Déconnexion</span>}
        </button>

      </aside>

      {showSettings && (
        <div className="apps-modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="apps-modal" onClick={e => e.stopPropagation()}>

            <div className="apps-modal-header">
              <h3>Applications visibles</h3>
              <button className="apps-modal-close" onClick={() => setShowSettings(false)}>✕</button>
            </div>

            <div className="apps-modal-content">
              {accessibleApps.map(app => (
                <label key={app.key} className="app-checkbox-line">
                  <input
                    type="checkbox"
                    checked={app.native || visibleApps.includes(app.key)}
                    disabled={app.native}
                    onChange={() => toggleApp(app.key)}
                  />
                  <span>{app.icon}</span>
                  <span>{app.label}</span>
                </label>
              ))}
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
